import {
    PortfolioEnterpriseProductConfigurationEntriesFilters,
    PortfolioEnterpriseProductConfigurationEntry,
    useRepositorySuspenseQuery,
} from '@mews-commander/data-access';
import { lt, useFeatureFlag } from '@mews/framework';
import {
    ActionToolbarButton,
    EmptyStateProps,
    IconName,
    Link,
    Stack,
    StackItem,
    Table,
    TableActiveColumnFilters,
    TableHeader,
    TableProvider,
    useFullRowSelection,
    useTableState,
} from '@optimus-web/core';
import { FeatureFlag } from '@mews-commander/enums';
import { BulkAddToProperty } from '../tables/CentralProductTable/BulkAddToProperty';
import { EnterprisesProductContextProvider } from '../../context/EnterprisesProductContext';
import { BulkActionToolbar } from '../_shared/BulkActionToolbar';
import { useBulkUpdateEnterpriseProductPricingDialogForm } from '../forms/useBulkUpdateEnterpriseProductPricingDialogForm';
import { useColumns } from './columns';

interface ProductPropertiesTabContentProps {
    centralProductId: string;
    centralProductName: string;
    assignedPropertyIds: string[];
}

export const CentralProductProperties = ({
    centralProductId,
    centralProductName,
    assignedPropertyIds,
}: ProductPropertiesTabContentProps) => {
    const columns = useColumns(centralProductName);

    const [tableState, setTableState] = useTableState<
        PortfolioEnterpriseProductConfigurationEntry,
        PortfolioEnterpriseProductConfigurationEntriesFilters
    >({
        pagination: {
            pageSize: 25,
        },
    });

    const { data, isFetching } = useRepositorySuspenseQuery('portfolioEnterpriseProducts', 'getConfigurationEntries', {
        ...tableState,
        centralProductId,
    });

    const tableEmptyState: EmptyStateProps = {
        primaryText: lt('CentralProductPropertiesEmptyStatePrimaryText'),
        secondaryText: lt('CentralProductPropertiesEmptyStateSecondaryText'),
        maxWidth: '400px',
        action: (
            <Stack vertical spacing="300" itemAlignment="center">
                <StackItem>
                    <BulkAddToProperty propertyIds={assignedPropertyIds} productId={centralProductId} />
                </StackItem>
                <Link standalone>{lt('LearnMoreAboutProducts')}</Link>
            </Stack>
        ),
    };

    const isBulkEditEnterpriseProductsEnabled = useFeatureFlag(FeatureFlag.bulkEditEnterpriseProductsEnabled);
    const { selectedRowsData, rowSelection, setRowSelection, resetRowSelection } = useFullRowSelection(
        data.data,
        'enterpriseProductId'
    );

    const [renderProductDialog, toggleProductDialog] = useBulkUpdateEnterpriseProductPricingDialogForm({
        centralProductId,
        enterpriseIds: selectedRowsData.map(({ property }) => property.id),
    });

    return (
        <Stack vertical>
            <TableProvider columns={columns} state={tableState} onStateChange={setTableState}>
                <EnterprisesProductContextProvider>
                    <TableHeader
                        title={lt('PropertiesNavigation')}
                        subTitle={data.totalRowCount}
                        rightSide={
                            <BulkAddToProperty
                                buttonVariant="tertiary"
                                propertyIds={assignedPropertyIds}
                                productId={centralProductId}
                            />
                        }
                    />
                    <TableActiveColumnFilters />
                    {isBulkEditEnterpriseProductsEnabled ? (
                        <BulkActionToolbar
                            resetRowSelection={resetRowSelection}
                            rightSide={
                                <ActionToolbarButton
                                    icon={IconName.Edit}
                                    disabled={!selectedRowsData.length || selectedRowsData.length > 50}
                                    onClick={toggleProductDialog}
                                >
                                    {lt('EnterpriseProductTableUpdatePricingButtonLabel')}
                                </ActionToolbarButton>
                            }
                            selectedRows={selectedRowsData.length}
                        />
                    ) : null}
                    <Table
                        fixedFirstColumn
                        data={data.data}
                        amountOfRows={data.totalRowCount}
                        uniqueRowId="enterpriseProductId"
                        emptyState={tableEmptyState}
                        loading={isFetching}
                        rowSelection={
                            isBulkEditEnterpriseProductsEnabled
                                ? {
                                      rowSelection,
                                      onRowSelectionChange: setRowSelection,
                                  }
                                : undefined
                        }
                    />
                </EnterprisesProductContextProvider>
            </TableProvider>
            {renderProductDialog()}
        </Stack>
    );
};
